import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatString } from "../../../common/formatString";
import { useErrorHandler } from "../../../common/hooks";
import { InputSelectModel } from "../../Models/InputSelectModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const InputSelectComponent: React.FC<IComponentProps<InputSelectModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();
  const [value, setValue] = useState<any>(null);
  const [originalValue, setOriginalValue] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<any[]>([]);
  const [optionsPending, setOptionsPending] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const componentLayoutSx = getComponentLayout(component);

  const handleLoadItems = async (searchText?: string) => {
    if (!component.Items) {
      console.warn("GetItems expression is not defined.");
      return;
    }

    setOptionsPending(true);
    try {
      const eventContext = context.CreateChild();
      eventContext.VariableScope.set("$event", { text: searchText || "" });

      const result = await eventContext.Evaluator.evaluate(component.Items, eventContext);

      if (result === undefined || result === null) {
        setOptions([]);
        return;
      }

      if (!Array.isArray(result)) {
        console.error("GetItems expression did not return an array:", result);
        throw new Error("GetItems expression did not return an array: " + result);
      }

      console.log("[InputSelectComponent] Loaded options:", result);
      setOptions(result);
    } catch (error) {
      errorHandler(error);
      setOptions([]);
    } finally {
      setOptionsPending(false);
    }
  };

  // Load options on start
  useEffect(() => {
    handleLoadItems();
  }, [component.Items]);

  // Load options when input text changes (with debounce)
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to execute after 300ms
    debounceTimerRef.current = setTimeout(() => {
      handleLoadItems(inputValue);
    }, 300);

    // Cleanup on unmount or when inputValue changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue]);

  // Load initial value from component.Item on first render
  useEffect(() => {
    const loadInitialValue = async () => {
      if (!component.Value) return;

      try {
        let result = await context.Evaluator.evaluate(component.Value, context);

        if (result === undefined && component.DefaultValue) {
          result = await context.Evaluator.evaluate(component.DefaultValue, context);
          console.log("[InputSelectComponent] Loading default value for component", result);

          context.Evaluator.assign(component.Value, context, result);
        }

        setValue(result);
        setOriginalValue(result);
      } catch (error) {
        errorHandler(error);
      }
    };

    loadInitialValue();
  }, [component.Value, component.DefaultValue]);

  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);

    if (!component.ItemSelected && component.Value) {
      // If no ItemSelected handler, update component.Value directly
      context.Evaluator.assign(component.Value, context, newValue);
    }

    // Clear input text when no selection
    if (!newValue) {
      setInputValue("");
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleOpen = () => {
    // When dropdown opens, clear input text and load options with empty search
    setInputValue("");
    if (options.length === 0 && !optionsPending) {
      handleLoadItems("");
    }
  };

  const handleSave = useCallback(async () => {
    if (!component.ItemSelected || !value) return;

    setIsUpdating(true);
    try {
      const eventContext = context.CreateChild();
      eventContext.VariableScope.set("$event", value);

      await eventContext.Evaluator.evaluate(component.ItemSelected, eventContext);

      // Update Item if defined
      if (component.Value) {
        console.log("[InputSelectComponent] Saving selected item to component.Item", value);
        context.Evaluator.assign(component.Value, context, value);
      }

      // Update original value after successful save
      setOriginalValue(value);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsUpdating(false);
    }
  }, [value, component, context, errorHandler]);

  const handleCancel = useCallback(() => {
    setValue(originalValue);
  }, [originalValue]);

  const hasChanged = value !== originalValue && (value !== null || originalValue !== null);

  const getItemKey = useCallback(
    (item: any) => {
      const keyField = component.ItemKeyField || "Id";
      return item[keyField];
    },
    [component.ItemKeyField]
  );

  const getItemLabel = useCallback(
    (item: any) => {
      const itemLabel = component.ItemLabel || "{Label}";
      return formatString(itemLabel, item);
    },
    [component.ItemLabel, getItemKey]
  );

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={componentLayoutSx} paddingTop={1} paddingBottom={1}>
      {component.Typeahead ? (
        <Autocomplete
          value={value}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onOpen={handleOpen}
          options={options}
          fullWidth
          size="small"
          getOptionKey={getItemKey}
          getOptionLabel={getItemLabel}
          loading={optionsPending}
          disabled={isUpdating}
          renderInput={(params) => <TextField {...params} label={component.Label} />}
          sx={{ flex: 1 }}
        />
      ) : (
        <FormControl variant="standard" fullWidth sx={{ flex: 1 }} required={component.IsRequired}>
          <InputLabel required={component.IsRequired}>{component.Label}</InputLabel>
          <Select
            value={value || ""}
            defaultValue={value || ""}
            onChange={(event) => handleChange(event, event.target.value)}
            label={component.Label}
            multiple={component.IsMultiSelect}
            disabled={isUpdating || optionsPending}
          >
            {options.map((item) => (
              <MenuItem key={getItemKey(item)} value={getItemKey(item)}>
                {getItemLabel(item)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {component.ItemSelected && hasChanged && (
        <>
          <IconButton onClick={handleSave} color="primary" disabled={isUpdating} loading={isUpdating} size="small">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={handleCancel} disabled={isUpdating} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Stack>
  );
};

export default InputSelectComponent;
